import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { TransactionComboboxControl } from "components/Transaction/TransactionColumn";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { MultiValue, SingleValue } from "react-select";
import { useUpdateBankAccountantAccessMutation } from "store/apis/bankConnections";
import { ENTITY_BANK_CONNECTION_DETAILS } from "types/Models/bookkeepingTracker";
import { BackendError } from "types/utils/error";
import { BankConnectionStatus } from "./BankConnectionStatus";
import classNames from "classnames";
import { Divider } from "components/design/Divider";
import { BANK_ACCOUNT_STATUS } from "constants/dataSource";
import { Button } from "components/DesignSystem/Button/Button";

export const bankAccessOptions = [
  {
    label: (
      <div className="t-flex t-gap-1 t-items-center">
        <div className="t-h-1.5 t-w-1.5 t-rounded-full t-bg-dark_green" />
        <div className="">Yes</div>
      </div>
    ),
    value: "true",
  },
  {
    label: (
      <div className="t-flex t-gap-1 t-items-center">
        <div className="t-h-1.5 t-w-1.5 t-rounded-full t-bg-red" />
        <div className="">No</div>
      </div>
    ),
    value: "false",
  },
];

type connectionDetail = ENTITY_BANK_CONNECTION_DETAILS & {
  entity_name?: string;
};

export const EntityBankConnections = ({
  connectionDetails,
  entityId,
  onRequestAccess,
  isRequestingAccess,
}: {
  connectionDetails: connectionDetail[];
  entityId: string;
  onRequestAccess?: (item: { entity_item_id: string; name: string }) => void;
  isRequestingAccess?: boolean;
}) => {
  const { alertToast, successToast } = useToast();
  const [updateBankAccountantAccess] = useUpdateBankAccountantAccessMutation();

  const { isAdmin } = useRoleBasedView();

  const handleBankAccessUpdate = async (
    entityItemId: string,
    accountantAccess?: string
  ) => {
    try {
      await updateBankAccountantAccess({
        entityId,
        accountantAccess: accountantAccess === "true",
        entityItemId: entityItemId,
      }).unwrap();
      successToast({ message: "Access updated successfully" });
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  if (connectionDetails && connectionDetails.length === 0) {
    return (
      <div className="t-flex t-h-40 t-w-full t-justify-center t-items-center">
        <div className="t-text-body-sm">No connections</div>
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col">
      {connectionDetails.map((connection, index) => {
        const selectedValue = bankAccessOptions.find(
          (option) => option.value === connection.accountant_access?.toString()
        );
        const isLastItem = index === connectionDetails.length - 1;

        return (
          <div
            key={connection.entity_item_id}
            className="t-flex t-flex-col t-gap-1"
          >
            <div>
              <div className="t-flex t-items-center t-justify-between">
                <div className="t-text-subtext t-text-text-100">
                  {connection.name}
                </div>
                <BankConnectionStatus
                  connected={connection.connection_available}
                  isClosed={connection.status === BANK_ACCOUNT_STATUS.CLOSED}
                />
              </div>

              {connection.entity_name && (
                <div className="t-text-body-sm t-text-text-30">
                  {connection.entity_name}
                </div>
              )}
            </div>
            <div>
              <div className="t-flex t-gap-3">
                <div className="t-text-body-sm t-text-text-30">
                  Accountant Access:
                </div>
                {isAdmin ? (
                  <div className="t-flex t-flex-col t-gap-1">
                    <div className="t-w-20">
                      <Combobox
                        size="small"
                        menuPlacement="auto"
                        creatable={false}
                        onChange={(
                          prop: SingleValue<OptionData> | MultiValue<OptionData>
                        ) =>
                          handleBankAccessUpdate(
                            connection.entity_item_id,
                            (prop as SingleValue<OptionData>)?.value
                          )
                        }
                        components={{
                          Control: TransactionComboboxControl,
                          ClearIndicator: () => null,
                        }}
                        options={bankAccessOptions}
                        defaultValue={selectedValue || null}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    {!connection.accountant_access &&
                      connection.accountant_access_reason && (
                        <span className="t-text-body-sm t-italic t-text-text-30">
                          Reason: {connection.accountant_access_reason}
                        </span>
                      )}
                    {onRequestAccess && (
                      <Button
                        customType="link"
                        size="small"
                        isLoading={Boolean(isRequestingAccess)}
                        disabled={Boolean(isRequestingAccess)}
                        onClick={() =>
                          onRequestAccess({
                            entity_item_id: connection.entity_item_id,
                            name: connection.name,
                          })
                        }
                      >
                        Request Access
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="t-flex t-gap-1 t-items-center">
                    <div
                      className={classNames("t-h-1.5 t-w-1.5 t-rounded-full", {
                        "t-bg-dark_green": connection.accountant_access,
                        "t-bg-red": !connection.accountant_access,
                      })}
                    />
                    <div className="t-text-body-sm t-text-text-100">
                      {connection.accountant_access ? "Yes" : "No"}
                    </div>
                  </div>
                )}
              </div>
              {!isLastItem && <Divider classNames="t-my-4" />}
            </div>
          </div>
        );
      })}
    </div>
  );
};
